
import React, { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthProvider";
import { Helmet } from "react-helmet";
import Layout from "@/components/layout/Layout";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, Building, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function OwnerNursingHomePage() {
  const { nhSlug } = useParams();
  const { user, isLoading: authLoading } = useAuth();
  const [nursingHome, setNursingHome] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const checkOwnershipAndLoadData = async () => {
      if (!user || !nhSlug) return;
      
      try {
        setIsLoading(true);
        
        // Check if user has an approved claim for this nursing home
        const { data: claims, error: claimsError } = await supabase
          .from('nursing_home_claims')
          .select('*')
          .eq('user_id', user.id)
          .eq('nursing_home_id', nhSlug)
          .eq('status', 'approved')
          .limit(1);
        
        if (claimsError) throw claimsError;
        
        if (!claims || claims.length === 0) {
          setIsAuthorized(false);
          return;
        }
        
        setIsAuthorized(true);
        
        // Fetch nursing home details
        const { data: home, error: homeError } = await supabase
          .from('nursing_homes')
          .select('*')
          .eq('nh_slug', nhSlug)
          .single();
        
        if (homeError) throw homeError;
        
        setNursingHome(home);
      } catch (err) {
        console.error("Error fetching nursing home data:", err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
        toast.error("Failed to load nursing home data");
      } finally {
        setIsLoading(false);
      }
    };

    if (user && !authLoading && nhSlug) {
      checkOwnershipAndLoadData();
    }
  }, [user, authLoading, nhSlug]);

  // Redirect to login if not authenticated
  if (!authLoading && !user) {
    return <Navigate to={`/auth/login?redirectTo=/owner/nursing-homes/${nhSlug}`} />;
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="container py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!isAuthorized) {
    return (
      <Layout>
        <div className="container py-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col items-center justify-center text-center">
                <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
                <h2 className="text-2xl font-bold mb-2">Not Authorized</h2>
                <p className="text-muted-foreground mb-4">
                  You don't have permission to manage this nursing home. Only verified owners can access this page.
                </p>
                <Button asChild className="bg-grandpal-green-dark hover:bg-grandpal-green-darkHover">
                  <Link to="/owner/dashboard">
                    Return to Dashboard
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (error || !nursingHome) {
    return (
      <Layout>
        <div className="container py-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col items-center justify-center text-center">
                <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
                <h2 className="text-2xl font-bold mb-2">Error Loading Data</h2>
                <p className="text-muted-foreground mb-4">
                  {error?.message || "Failed to load nursing home details"}
                </p>
                <Button onClick={() => window.location.reload()}>
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>{nursingHome.center_name} - Owner Management | Grandpal</title>
      </Helmet>

      <div className="container py-6 max-w-7xl">
        <div className="mb-6">
          <Link 
            to="/owner/dashboard"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </Link>
        </div>
        
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight mb-1">{nursingHome.center_name}</h1>
          <p className="text-muted-foreground">
            Manage your nursing home profile and inquiries
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Management Console
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-center text-muted-foreground py-12">
                Coming soon: Edit profile details, respond to inquiries, and view analytics.
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button variant="outline" className="w-full justify-start" asChild>
                <Link to={`/nursing-homes/${nursingHome.county_slug}/${nursingHome.nh_slug}`}>
                  <Eye className="h-4 w-4 mr-2" />
                  View Public Profile
                </Link>
              </Button>
              <Button variant="outline" className="w-full justify-start" disabled>
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit Profile Details
              </Button>
              <Button variant="outline" className="w-full justify-start" disabled>
                <Users className="h-4 w-4 mr-2" />
                View Inquiries
              </Button>
              <Button variant="outline" className="w-full justify-start" disabled>
                <BarChart className="h-4 w-4 mr-2" />
                View Analytics
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}

// Importing additional icons for the UI
function PencilIcon(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

function Users(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function BarChart(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" x2="12" y1="20" y2="10" />
      <line x1="18" x2="18" y1="20" y2="4" />
      <line x1="6" x2="6" y1="20" y2="16" />
    </svg>
  );
}

function Eye(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
